"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var DispatcherPayloadMeta_1 = require("../DispatcherPayloadMeta");
var UnitOfWork_1 = require("./UnitOfWork");
var map_like_1 = require("map-like");
var TransactionBeganPayload_1 = require("../payload/TransactionBeganPayload");
var TransactionEndedPayload_1 = require("../payload/TransactionEndedPayload");
/**
 * UseCaseUnitOfWork is a Unit of Work between UseCase and StoreGroup.
 * It aim to manager updating of StoreGroup via UseCase.
 */
var UseCaseUnitOfWork = /** @class */ (function () {
    function UseCaseUnitOfWork(args) {
        var _this = this;
        this.name = args.name;
        this.storeGroup = args.storeGroup;
        this.unitOfWork = new UnitOfWork_1.UnitOfWork(this.storeGroup);
        this.dispatcher = args.dispatcher;
        this.options = args.options;
        this.unsubscribeMap = new map_like_1.MapLike();
        this.isTransactionWorking = false;
        this.doesReflectActionAtLeastOne = false;
        if (this.options.autoCommit) {
            this.unitOfWork.onAddedCommitment(function () {
                _this.unitOfWork.commit();
            });
        }
    }
    Object.defineProperty(UseCaseUnitOfWork.prototype, "id", {
        /**
         * Unique id
         */
        get: function () {
            return this.unitOfWork.id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UseCaseUnitOfWork.prototype, "isDisposed", {
        /**
         * Is Unit of Work already disposed?
         */
        get: function () {
            return this.unitOfWork.isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UseCaseUnitOfWork.prototype, "size", {
        /**
         * current queued commitment size
         */
        get: function () {
            return this.unitOfWork.size;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UseCaseUnitOfWork.prototype, "workingUseCaseCount", {
        /**
         * count of current working useCases
         */
        get: function () {
            return this.unsubscribeMap.size;
        },
        enumerable: true,
        configurable: true
    });
    UseCaseUnitOfWork.prototype.beginTransaction = function () {
        var transaction = {
            id: this.id,
            name: this.name
        };
        this.isTransactionWorking = true;
        var payload = new TransactionBeganPayload_1.TransactionBeganPayload(this.name);
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            isTrusted: true,
            transaction: transaction
        });
        this.dispatcher.dispatch(payload, meta);
        this.unitOfWork.addCommitment({
            payload: payload,
            meta: meta,
            debugId: transaction.id
        });
    };
    /**
     * Begin transaction of the useCaseExecutor/UseCase
     */
    UseCaseUnitOfWork.prototype.open = function (useCaseExecutor) {
        var _this = this;
        var onDispatchOnUnitOfWork = function (payload, meta) {
            // If unit of work is working as transaction, overwrite meta.transaction
            if (_this.isTransactionWorking) {
                meta.transaction = {
                    id: _this.id,
                    name: _this.name
                };
            }
            // Notes: It is specific order for updating store logging
            // 1. Commit
            _this.unitOfWork.addCommitment({
                payload: payload,
                meta: meta,
                debugId: _this.id
            });
            // 2. Dispatch to Dispatcher
            _this.dispatcher.dispatch(payload, meta);
        };
        var unsubscribe = useCaseExecutor.onDispatch(onDispatchOnUnitOfWork);
        this.unsubscribeMap.set(useCaseExecutor, unsubscribe);
    };
    /**
     * Commit current queued payload to Committable StoreGroup.
     * After commit, prune current queue.
     *
     * ## Notes
     *
     * Do `commit()` and then this unit of work will be released.
     */
    UseCaseUnitOfWork.prototype.commit = function () {
        // transaction commit work only once
        if (this.doesReflectActionAtLeastOne) {
            throw new Error("Error(Transaction): This unit of work is already commit() or exit().\nNot to allow to do multiple commits in a transaction");
        }
        if (!this.isTransactionWorking) {
            this.unitOfWork.commit();
        }
        else {
            var commitment = this.createTransactionEndPayload();
            // Notes: It is specific order for updating store logging
            // 1. commit
            this.unitOfWork.addCommitment(commitment);
            this.unitOfWork.commit();
            // 2. dispatch
            this.dispatcher.dispatch(commitment.payload, commitment.meta);
            this.isTransactionWorking = false;
        }
        // commit flag
        this.doesReflectActionAtLeastOne = true;
    };
    UseCaseUnitOfWork.prototype.createTransactionEndPayload = function () {
        var transaction = {
            id: this.id,
            name: this.name
        };
        // payload, meta
        var payload = new TransactionEndedPayload_1.TransactionEndedPayload(this.name);
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            isTrusted: true,
            transaction: transaction
        });
        return {
            payload: payload,
            meta: meta,
            debugId: this.id
        };
    };
    /**
     * End transaction of the useCaseExecutor/UseCase
     */
    UseCaseUnitOfWork.prototype.close = function (useCaseExecutor) {
        var unsubscribe = this.unsubscribeMap.get(useCaseExecutor);
        if (typeof unsubscribe !== "function") {
            console.error("Warning(UnitOfWork): This UseCaseExecutor is not opened or already closed.", useCaseExecutor);
            return;
        }
        unsubscribe();
        this.unsubscribeMap.delete(useCaseExecutor);
    };
    UseCaseUnitOfWork.prototype.exit = function () {
        // transaction exit work only once
        if (this.doesReflectActionAtLeastOne) {
            throw new Error("Error(Transaction): This unit of work is already commit() or exit().\nDisallow to do multiple exit in a transaction");
        }
        this.doesReflectActionAtLeastOne = true;
        if (this.isTransactionWorking) {
            var commitment = this.createTransactionEndPayload();
            this.dispatcher.dispatch(commitment.payload, commitment.meta);
            this.isTransactionWorking = false;
        }
    };
    /**
     * Release this Unit of Work.
     * After released, can't commit this Unit of Work.
     *
     * Please call `exit` or `commit at once before releasing.
     */
    UseCaseUnitOfWork.prototype.release = function () {
        var _this = this;
        if (!this.options.autoCommit && !this.doesReflectActionAtLeastOne) {
            if (process.env.NODE_ENV !== "production") {
                console.error("Warning(UnitOfWork): Transaction(" + this.name + ") should be commit() or exit() at least one. \nIf you not want to commit, Please call `transactionContext.exit()` at end of transaction.\n");
            }
            this.exit();
        }
        this.unsubscribeMap.keys().forEach(function (useCaseExecutor) {
            _this.close(useCaseExecutor);
        });
        this.unitOfWork.release();
    };
    return UseCaseUnitOfWork;
}());
exports.UseCaseUnitOfWork = UseCaseUnitOfWork;
//# sourceMappingURL=UseCaseUnitOfWork.js.map


//////////////////
// WEBPACK FOOTER
// /home/travis/build/proofdict/proofdict/node_modules/almin/lib/src/UnitOfWork/UseCaseUnitOfWork.js
// module id = 480
// module chunks = 0